import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { Music, TrendingUp, Calendar, Star, Quote, CheckCircle2, Sparkles } from "lucide-react";
import { MarketingHeader } from "@/components/MarketingHeader";
import { MarketingFooter } from "@/components/MarketingFooter";
import { SEO } from "@/components/SEO";
import { FirstTimeModal } from "@/components/FirstTimeModal";
import { MarketingBackground } from "@/components/MarketingBackground";
import { BackgroundShapes } from "@/components/BackgroundShapes";
import heroImage from "@/assets/hero-vocal-studio.jpg";
import avatar1 from "@/assets/avatar-1.jpg";
import avatar2 from "@/assets/avatar-2.jpg";
import avatar3 from "@/assets/avatar-3.jpg";
import avatar4 from "@/assets/avatar-4.jpg";

const Index = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);

  useEffect(() => {
    checkUser();
    
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        const redirectPath = await getRoleBasedRedirect(session.user.id);
        navigate(redirectPath);
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const checkUser = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    setUser(user);
    if (user) {
      const redirectPath = await getRoleBasedRedirect(user.id);
      navigate(redirectPath);
    }
  };

  const getRoleBasedRedirect = async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId)
      .single();

    if (data?.role === "admin") return "/admin";
    if (data?.role === "coach") return "/coach";
    return "/booking";
  };

  const testimonials = [
    {
      name: "Sarah Johnson",
      role: "Professional Singer",
      content: "Vocalzing transformed my technique. The progress tracking kept me motivated!",
      rating: 5
    },
    {
      name: "Mike Chen",
      role: "Aspiring Artist",
      content: "Flexible scheduling and amazing coaching. I've improved more in 3 months than in years.",
      rating: 5
    },
    {
      name: "Emma Williams",
      role: "Voice Actor",
      content: "The analytics are incredible. I can see exactly where I'm improving.",
      rating: 5
    }
  ];

  const benefits = [
    "One-on-one personalized coaching",
    "Flexible session lengths (15-90 min)",
    "Real-time progress analytics",
    "Book anytime, anywhere",
    "Professional vocal guidance",
    "No long-term commitments"
  ];

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "EducationalOrganization",
    "name": "Vocalzing",
    "description": "Professional vocal coaching platform with flexible sessions and progress tracking",
    "url": "https://vocalzing.lovable.app",
    "logo": "https://vocalzing.lovable.app/favicon.png",
    "sameAs": [],
    "offers": {
      "@type": "Offer",
      "category": "Education",
      "priceCurrency": "USD",
      "price": "29.99",
      "priceRange": "$29.99 - $179.99"
    }
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      <MarketingBackground />
      <div className="pt-[73px]">
        <SEO
        title="Professional Vocal Lessons & Coaching Online"
        description="Book flexible vocal coaching sessions from 15 minutes to 90 minutes. Track your singing progress with detailed analytics. Professional guidance starting at $29.99."
        keywords="vocal lessons, singing coach, voice training, online vocal coaching, music lessons, singing lessons, vocal technique, progress tracking"
        canonical="https://vocalzing.lovable.app/"
        jsonLd={organizationSchema}
      />
      <MarketingHeader user={user} />
      <FirstTimeModal />

      {/* Hero Section */}
      <section className="relative" aria-label="Hero section">
        <BackgroundShapes variant="hero" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 z-0" aria-hidden="true" />
        <div className="container mx-auto px-4 py-24 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6 animate-fade-in">
              <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium">
                <Sparkles className="w-4 h-4" />
                Professional Vocal Training
              </div>
              <h1 className="text-5xl md:text-6xl font-bold leading-tight">
                Unlock Your
                <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  Vocal Potential
                </span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Professional vocal coaching tailored to your goals. Book flexible sessions from 15 minutes to 1.5 hours and track your progress with detailed analytics.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button 
                  variant="hero" 
                  size="lg"
                  className="text-lg px-8"
                  onClick={() => navigate(user ? "/booking" : "/auth")}
                >
                  Book Your First Lesson
                </Button>
                {!user && (
                  <Button variant="outline" size="lg" onClick={() => navigate("/auth")}>
                    Sign Up Free
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-6 pt-4">
                <div className="flex -space-x-2">
                  {[avatar1, avatar2, avatar3, avatar4].map((avatar, i) => (
                    <img 
                      key={i} 
                      src={avatar} 
                      alt={`Happy customer ${i + 1}`}
                      className="w-10 h-10 rounded-full border-2 border-background object-cover"
                    />
                  ))}
                </div>
                <div>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <p className="text-sm text-muted-foreground">500+ happy students</p>
                </div>
              </div>
            </div>
            <div className="relative animate-slide-up">
              <img 
                src={heroImage} 
                alt="Professional vocal studio with microphone and equipment" 
                className="rounded-2xl shadow-2xl border border-border/40"
              />
              <div className="absolute -bottom-6 -left-6 bg-card border border-border/40 rounded-xl p-4 shadow-xl backdrop-blur-sm animate-fade-in">
                <p className="text-sm text-muted-foreground">Starting from</p>
                <p className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  $29.85
                </p>
                <p className="text-xs text-muted-foreground">per 15 min session</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="container mx-auto px-4 py-16 bg-muted/30 -mt-8 rounded-3xl" aria-label="Benefits section">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Everything You Need to Succeed</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {benefits.map((benefit, index) => (
              <div key={index} className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border/40">
                <CheckCircle2 className="w-5 h-5 text-primary flex-shrink-0" />
                <span className="text-foreground">{benefit}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="container mx-auto px-4 py-24 relative" aria-label="Features section">
        <BackgroundShapes variant="features" />
        <h2 className="text-3xl font-bold text-center mb-4 relative z-10">
          Why Choose Vocalzing?
        </h2>
        <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
          Join hundreds of students who have transformed their voices with our proven coaching method
        </p>
        <div className="grid md:grid-cols-3 gap-8">
          <Card className="border-border/40 shadow-lg hover-lift cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-primary to-secondary flex items-center justify-center mb-4">
                <Calendar className="w-6 h-6 text-white" />
              </div>
              <CardTitle>Flexible Booking</CardTitle>
              <CardDescription className="text-base">
                Choose sessions from 15 minutes to 1.5 hours that fit your schedule. Book instantly without waiting.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-border/40 shadow-lg hover-lift cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-primary to-secondary flex items-center justify-center mb-4">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <CardTitle>Track Progress</CardTitle>
              <CardDescription className="text-base">
                Visual analytics to monitor your vocal journey and improvement over time with detailed insights.
              </CardDescription>
            </CardHeader>
          </Card>

          <Card className="border-border/40 shadow-lg hover-lift cursor-pointer">
            <CardHeader>
              <div className="w-12 h-12 rounded-lg bg-gradient-to-r from-primary to-secondary flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-white" />
              </div>
              <CardTitle>Professional Coaching</CardTitle>
              <CardDescription className="text-base">
                Personalized guidance from experienced vocal instructors who care about your success.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container mx-auto px-4 py-24 bg-gradient-to-br from-muted/50 to-transparent rounded-3xl relative" aria-label="Testimonials section">
        <BackgroundShapes variant="testimonials" />
        <h2 className="text-3xl font-bold text-center mb-4 relative z-10">Loved by Students Worldwide</h2>
        <p className="text-center text-muted-foreground mb-12">See what our students have to say</p>
        <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
          {testimonials.map((testimonial, index) => (
            <Card key={index} className="border-border/40 shadow-lg hover-lift">
              <CardHeader>
                <div className="flex gap-1 mb-3">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-primary text-primary" />
                  ))}
                </div>
                <Quote className="w-8 h-8 text-primary/40 mb-2" />
                <CardDescription className="text-base text-foreground italic">
                  "{testimonial.content}"
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="font-semibold">{testimonial.name}</p>
                <p className="text-sm text-muted-foreground">{testimonial.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="container mx-auto px-4 py-24 relative" aria-label="Pricing section">
        <BackgroundShapes variant="pricing" />
        <div className="max-w-4xl mx-auto text-center space-y-8 relative z-10">
          <h2 className="text-4xl font-bold">Simple, Transparent Pricing</h2>
          <p className="text-xl text-muted-foreground">
            Pay only for the time you need. No subscriptions, no hidden fees.
          </p>
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="border-border/40 hover-lift">
              <CardHeader className="space-y-4">
                <div className="text-sm text-muted-foreground">Starter</div>
                <CardTitle className="text-2xl">15 min</CardTitle>
                <p className="text-4xl font-bold text-primary">$29.85</p>
                <CardDescription>Perfect for quick sessions</CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-primary shadow-lg scale-105 hover-lift relative overflow-hidden">
              <div className="absolute top-0 right-0 bg-primary text-white px-3 py-1 text-xs font-bold">
                POPULAR
              </div>
              <CardHeader className="space-y-4">
                <div className="text-sm text-muted-foreground">Standard</div>
                <CardTitle className="text-2xl">45 min</CardTitle>
                <p className="text-4xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                  $89.55
                </p>
                <CardDescription>Most popular choice</CardDescription>
              </CardHeader>
            </Card>
            <Card className="border-border/40 hover-lift">
              <CardHeader className="space-y-4">
                <div className="text-sm text-muted-foreground">Extended</div>
                <CardTitle className="text-2xl">90 min</CardTitle>
                <p className="text-4xl font-bold text-primary">$179.10</p>
                <CardDescription>Deep-dive sessions</CardDescription>
              </CardHeader>
            </Card>
          </div>
          <Button 
            variant="hero" 
            size="lg"
            className="text-lg px-12"
            onClick={() => navigate(user ? "/booking" : "/auth")}
          >
            Start Your Journey Today
          </Button>
        </div>
      </section>

      <MarketingFooter />
      </div>
    </div>
  );
};

export default Index;
